import { useState, useMemo } from 'react';
import { BottomSheet } from '@/components/base/BottomSheet';
import { showToast } from '@/components/base/Toast';
import { getContractsByFarmer, getLimitsByFarmer } from '@/store';
import { useStore } from '@/hooks/useStore';
import { createLimitRequest } from '@/api/lending';
import { ApiError } from '@/api/client';
import { refreshLimit } from '@/store/limits';
import { formatCurrencyCompact, formatCurrencyFull } from '@/utils/formatters';

interface ApplyLimitSheetProps {
  open: boolean;
  onClose: () => void;
  farmerId: string;
  farmerName: string;
  /** Called after a request is filed, so the caller can refresh its own list. */
  onSubmitted?: () => void;
}

// eKYC is only required to actually apply for a loan ("vay"). Asking for a credit
// limit files a LimitRequest for staff to decide and moves no money by itself, so
// this sheet starts directly at the form.
type Step = 'form' | 'confirm';

export function ApplyLimitSheet({ open, onClose, farmerId, farmerName, onSubmitted }: ApplyLimitSheetProps) {
  const [step, setStep] = useState<Step>('form');
  const [contractId, setContractId] = useState('');
  const [limitAmount, setLimitAmount] = useState('');
  const [limitReason, setLimitReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // useStore(), not getState(): contracts and limits come from KeyedAsyncCache, so
  // the first read returns [] and starts a fetch, then calls notify() when it
  // lands. getState() is a plain read with no subscription, so this sheet never
  // heard about that and kept the empty list — which is why the contract dropdown
  // was empty on a fresh session and only filled in once the user had visited the
  // contracts page and come back, by which point the cache was warm.
  // Called for the subscription only, same as HtxFarmerPage does.
  useStore();

  // Read straight through on each render rather than useMemo. The memo was keyed
  // on farmerId alone, so it pinned the first (empty) result for the life of the
  // sheet — and the store snapshot cannot be added as a dep without tripping
  // react-hooks/exhaustive-deps, since the callback never mentions it: the
  // staleness comes from module state these getters read. Both lists are a
  // handful of rows per farmer, so there is nothing worth memoising.
  const contracts = getContractsByFarmer(farmerId).filter((c) => c.status === 'Đang hiệu lực');
  const moneyLimits = getLimitsByFarmer(farmerId).filter((l) => l.loaiDinhMuc === 'Vay vốn sản xuất');

  const hanMucTienHienTai = useMemo(() => {
    const total = moneyLimits.reduce((s, l) => s + l.hanMuc, 0);
    const used = moneyLimits.reduce((s, l) => s + l.daSuDung, 0);
    return { total, used, remaining: Math.max(0, total - used) };
  }, [moneyLimits]);

  const selectedContract = contracts.find((c) => c.id === contractId);
  const proposedAmount = Number(limitAmount);
  const exceedsContract = selectedContract && proposedAmount > selectedContract.giaTri;
  const exceedsCurrentLimit = proposedAmount <= 0 ? false : (proposedAmount <= hanMucTienHienTai.total);

  const handleSubmit = () => {
    const amount = Number(limitAmount);
    if (!limitAmount || Number.isNaN(amount) || amount <= 0) {
      showToast('Số tiền không hợp lệ', 'warning');
      return;
    }
    if (!contractId) {
      showToast('Vui lòng chọn hợp đồng', 'warning');
      return;
    }
    // This used to show a toast telling the farmer to contact their HTX and record
    // nothing at all — the request existed nowhere, which is exactly why it was
    // impossible to tell where a submission had gone.
    setSubmitting(true);
    createLimitRequest({ farmerId, contractId, requestedAmountVnd: amount, reason: limitReason.trim() || undefined })
      .then(() => {
        // The decision (and the grant it performs) is Agrida staff's, so say so
        // rather than implying the HTX will action it.
        showToast('Đã gửi yêu cầu hạn mức. Hợp tác xã và Agrida sẽ xem xét và phản hồi.', 'success');
        refreshLimit(farmerId);
        onSubmitted?.();
        resetAndClose();
      })
      .catch((e) => {
        setSubmitting(false);
        showToast(e instanceof ApiError ? e.message : 'Không gửi được yêu cầu hạn mức, vui lòng thử lại', 'error');
      });
  };

  const resetAndClose = () => {
    setStep('form');
    setContractId('');
    setLimitAmount('');
    setLimitReason('');
    setSubmitting(false);
    onClose();
  };

  const steps: Step[] = ['form', 'confirm'];

  return (
    <BottomSheet open={open} onClose={resetAndClose} title="Xin hạn mức tiền">
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2 mb-4">
        {steps.map((s, idx) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              step === s ? 'bg-amber-600 text-white' : idx < steps.indexOf(step) ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400'
            }`}>
              {idx < steps.indexOf(step) ? <i className="ri-check-line text-xs" /> : (idx + 1)}
            </span>
            <span className={`text-[11px] font-medium ${step === s ? 'text-amber-600' : 'text-gray-400'}`}>
              {s === 'form' ? 'Nhập hạn mức' : 'Xác nhận'}
            </span>
            {idx < 1 && <div className="w-6 h-px bg-gray-200" />}
          </div>
        ))}
      </div>

      {step === 'form' && (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap">Hạn mức tiền</span>
          </div>

          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-[10px] text-gray-400 mb-1">Hạn mức tiền hiện tại</p>
            <p className="text-sm font-bold text-gray-900">{formatCurrencyCompact(hanMucTienHienTai.total)}</p>
            {hanMucTienHienTai.remaining > 0 && (
              <p className="text-[10px] text-agrida-600 mt-0.5">Còn lại: {formatCurrencyCompact(hanMucTienHienTai.remaining)}</p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Chọn hợp đồng</label>
            <select
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-amber-500"
            >
              <option value="">-- Chọn hợp đồng --</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.soHopDong} ({c.cayTrong} {c.giong}) - {formatCurrencyCompact(c.giaTri)}
                </option>
              ))}
            </select>
            {selectedContract && (
              <p className="text-[10px] text-gray-400 mt-1">Giá trị hợp đồng: {formatCurrencyCompact(selectedContract.giaTri)}</p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Hạn mức tiền đề xuất (VNĐ)</label>
            <input
              type="number"
              value={limitAmount}
              onChange={(e) => setLimitAmount(e.target.value)}
              placeholder="VD: 200000000"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-amber-500"
            />
            {limitAmount && !Number.isNaN(proposedAmount) && (
              <p className="mt-1 text-[10px] italic text-gray-400">{formatCurrencyFull(proposedAmount)}</p>
            )}
            {exceedsContract && (
              <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                <i className="ri-error-warning-line" /> Vượt giá trị hợp đồng, cần nhập lý do bên dưới
              </p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">
              Lý do {exceedsContract ? <span className="text-amber-600">(bắt buộc khi vượt hợp đồng)</span> : ''}
            </label>
            <textarea
              value={limitReason}
              onChange={(e) => setLimitReason(e.target.value)}
              placeholder="VD: Cần mở rộng diện tích canh tác, đầu tư hệ thống tưới tiêu mới..."
              rows={3}
              maxLength={500}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-amber-500 resize-none"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={() => {
                if (exceedsContract && !limitReason.trim()) {
                  showToast('Vui lòng nhập lý do khi vượt giá trị hợp đồng', 'warning');
                  return;
                }
                setStep('confirm');
              }}
              disabled={!limitAmount || proposedAmount <= 0 || !contractId || (exceedsContract && !limitReason.trim())}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Tiếp tục
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-bold text-amber-800 mb-2">Xác nhận yêu cầu hạn mức tiền</p>
            <div className="space-y-2 text-[11px]">
              <div className="flex justify-between">
                <span className="text-gray-500">Nông hộ</span>
                <span className="font-medium text-gray-900">{farmerName}</span>
              </div>
              {selectedContract && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Hợp đồng</span>
                  <span className="font-medium text-gray-900">{selectedContract.soHopDong} - {formatCurrencyCompact(selectedContract.giaTri)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Hạn mức hiện tại</span>
                <span className="font-medium text-gray-900">{formatCurrencyCompact(hanMucTienHienTai.total)}</span>
              </div>
              <div className="flex justify-between border-t border-amber-200 pt-2">
                <span className="text-gray-700 font-semibold">Hạn mức đề xuất</span>
                <span className="font-bold text-amber-700 text-sm">{formatCurrencyCompact(proposedAmount)}</span>
              </div>
              {limitReason && (
                <div className="border-t border-amber-200 pt-2">
                  <span className="text-gray-500">Lý do:</span>
                  <p className="text-gray-700 mt-0.5">{limitReason}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep('form')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              ← Sửa lại
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors whitespace-nowrap"
            >
              {submitting ? 'Đang gửi...' : 'Xác nhận gửi'}
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
